import { AxiosResponse } from "axios";
import { RtkEmptySplitApi } from "../../types";
import { AIWorkflowsApiTag } from "./tags";
import { AnonymousSession, KnowledgeItem, KnowledgeItemType } from "./types";
import { ChatBotMessageCTAToolResult } from "../integrations/models/ChatBotMessage";
import { patchKnowledgeList } from "./utils";

export const addAIWorkflowsApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [AIWorkflowsApiTag.info],
  });

  const updateKnowledgeCache = <T extends KnowledgeItemType>({
    entityId,
    agentId,
    knowledgeType,
    recipe,
  }: {
    entityId: string;
    agentId: string;
    knowledgeType: T;
    recipe: (draft: KnowledgeItem<T>[]) => void;
  }) =>
    apiWithTags.util.updateQueryData(
      "getKnowledge" as never,
      { entityId, agentId, type: knowledgeType } as never,
      recipe as never
    );

  const aiWorkflowsApi = apiWithTags.injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      uploadTextKnowledge: builder.mutation<
        KnowledgeItem<"text">["data"],
        {
          entityId: string;
          agentId: string;
          title: string;
          content: string;
        }
      >({
        query: ({ entityId, agentId, title, content }) => {
          const formData = new FormData();
          formData.append("type", "text");
          formData.append("content", content);
          formData.append("title", title);

          return {
            url: `/api/auth/${entityId}/agent/${agentId}/knowledge/create`,
            method: "POST",
            data: formData,
            silentError: true,
          };
        },
        onQueryStarted: patchKnowledgeList({
          knowledgeType: "text",
          patchMode: "prepend",
          updateKnowledgeCache,
        }),
        invalidatesTags: [AIWorkflowsApiTag.info],
      }),
      updateTextKnowledge: builder.mutation<
        KnowledgeItem<"text">["data"],
        {
          entityId: string;
          agentId: string;
          title: string;
          content: string;
          id: string;
        }
      >({
        query: ({ entityId, agentId, title, content, id }) => {
          const formData = new FormData();
          formData.append("id", id);
          formData.append("source", content);
          formData.append("title", title);

          return {
            url: `/api/auth/${entityId}/agent/${agentId}/knowledge/update`,
            method: "PUT",
            data: formData,
            silentError: true,
          };
        },
        onQueryStarted: patchKnowledgeList<
          "text",
          { entityId: string; agentId: string; id: string }
        >({
          knowledgeType: "text",
          patchMode: "replace",
          resolveSourceId: (mutationArgs) => mutationArgs.id,
          updateKnowledgeCache,
        }),
        invalidatesTags: [AIWorkflowsApiTag.info],
      }),
      uploadQuestionKnowledge: builder.mutation<
        KnowledgeItem<"ques">["data"],
        {
          entityId: string;
          agentId: string;
          content: { question: string; answer: string }[];
        }
      >({
        query: ({ entityId, agentId, content }) => {
          const formData = new FormData();
          formData.append("type", "ques");
          formData.append("content", JSON.stringify(content));

          return {
            url: `/api/auth/${entityId}/agent/${agentId}/knowledge/create`,
            method: "POST",
            data: formData,
            silentError: true,
          };
        },
        onQueryStarted: patchKnowledgeList({
          knowledgeType: "ques",
          patchMode: "prepend",
          updateKnowledgeCache,
        }),
        invalidatesTags: [AIWorkflowsApiTag.info],
      }),
      updateQuestionKnowledge: builder.mutation<
        KnowledgeItem<"ques">["data"],
        {
          entityId: string;
          agentId: string;
          content: { question: string; answer: string }[];
          id: string;
        }
      >({
        query: ({ entityId, agentId, content, id }) => {
          const formData = new FormData();
          formData.append("id", id);
          formData.append("source", JSON.stringify(content));

          return {
            url: `/api/auth/${entityId}/agent/${agentId}/knowledge/update`,
            method: "PUT",
            data: formData,
            silentError: true,
          };
        },
        onQueryStarted: patchKnowledgeList<
          "ques",
          { entityId: string; agentId: string; id: string }
        >({
          knowledgeType: "ques",
          patchMode: "replace",
          resolveSourceId: (mutationArgs) => mutationArgs.id,
          updateKnowledgeCache,
        }),
        invalidatesTags: [AIWorkflowsApiTag.info],
      }),
      uploadFileKnowledge: builder.mutation<
        KnowledgeItem<"pdf" | "docx">["data"],
        {
          entityId: string;
          agentId: string;
          type: Extract<KnowledgeItemType, "pdf" | "docx">;
          file: File;
        }
      >({
        query: ({ entityId, agentId, type, file }) => {
          const formData = new FormData();
          formData.append("type", type);
          formData.append("file", file);

          return {
            url: `/api/auth/${entityId}/agent/${agentId}/knowledge/create`,
            method: "POST",
            data: formData,
            silentError: true,
          };
        },
        onQueryStarted: patchKnowledgeList<
          Extract<KnowledgeItemType, "pdf" | "docx">,
          {
            entityId: string;
            agentId: string;
            type: Extract<KnowledgeItemType, "pdf" | "docx">;
          }
        >({
          knowledgeType: (mutationArgs) => mutationArgs.type,
          patchMode: "prepend",
          updateKnowledgeCache,
        }),
        invalidatesTags: [AIWorkflowsApiTag.info],
      }),
      getListLinks: builder.query<
        {
          info: { logo_url: string; primary_color: string; title: string };
          links: { links: string[]; total_links: number; url: string };
        },
        {
          entityId: string;
          agentId: string;
          params: {
            url: string;
          };
        }
      >({
        query: ({ entityId, agentId, params }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge/links`,
          params,
        }),
      }),
      uploadLinkKnowledge: builder.mutation<
        KnowledgeItem<"links">["data"],
        {
          entityId: string;
          agentId: string;
          url: string;
          links: string[];
        }
      >({
        query: ({ entityId, agentId, url, links }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge/link/create`,
          method: "POST",
          data: {
            url,
            links,
          },
          silentError: true,
        }),
        onQueryStarted: patchKnowledgeList({
          knowledgeType: "links",
          patchMode: "prepend",
          updateKnowledgeCache,
        }),
        invalidatesTags: [AIWorkflowsApiTag.info],
      }),
      updateLinkKnowledge: builder.mutation<
        KnowledgeItem<"links">["data"],
        {
          entityId: string;
          agentId: string;
          source: string[];
          id: string;
        }
      >({
        query: ({ entityId, agentId, source, id }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge/update`,
          method: "PUT",
          data: {
            source,
            id,
          },
          silentError: true,
        }),
        onQueryStarted: patchKnowledgeList<
          "links",
          { entityId: string; agentId: string; id: string }
        >({
          knowledgeType: "links",
          patchMode: "replace",
          resolveSourceId: (mutationArgs) => mutationArgs.id,
          updateKnowledgeCache,
        }),
        invalidatesTags: [AIWorkflowsApiTag.info],
      }),
      getKnowledgeInfo: builder.query<
        {
          info: {
            total_size: number | null;
            last_updated: string | null;
            total_items: number;
          };
        },
        {
          entityId: string;
          agentId: string;
        }
      >({
        query: ({ entityId, agentId }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge/info`,
        }),
        providesTags: [AIWorkflowsApiTag.info],
      }),
      getKnowledge: builder.query<
        KnowledgeItem[],
        { entityId: string; agentId: string; type: KnowledgeItemType }
      >({
        query: ({ entityId, agentId, type }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge/list/v2`,
          params: { type },
        }),
      }),
      deleteKnowledge: builder.mutation<
        void,
        {
          entityId: string;
          agentId: string;
          type: KnowledgeItemType;
          source_id: string;
        }
      >({
        query: ({ entityId, agentId, source_id }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge`,
          method: "DELETE",
          params: {
            source_id,
          },
          silentError: true,
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            aiWorkflowsApi.util.updateQueryData(
              "getKnowledge",
              {
                entityId: args.entityId,
                agentId: args.agentId,
                type: args.type,
              },
              (knowledgeListDraft) =>
                knowledgeListDraft.filter(
                  (item) => item.data.id !== args.source_id
                )
            )
          );
          queryFulfilled.catch(() => {
            patchResult.undo();
          });
        },
        invalidatesTags: [AIWorkflowsApiTag.info],
      }),
      deleteParticularLink: builder.mutation<
        void,
        {
          entityId: string;
          agentId: string;
          source_id: string;
          url: string;
        }
      >({
        query: ({ entityId, agentId, source_id, url }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge`,
          method: "DELETE",
          params: {
            source_id,
            url,
          },
          silentError: true,
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const updateKnowledgeList = dispatch(
            aiWorkflowsApi.util.updateQueryData(
              "getKnowledge",
              {
                entityId: args.entityId,
                agentId: args.agentId,
                type: "links",
              },
              (knowledgeListDraft) =>
                (knowledgeListDraft as KnowledgeItem<"links">[]).map((item) =>
                  item.data.id === args.source_id
                    ? {
                        ...item,
                        data: {
                          ...item.data,
                          source: item.data.source.filter(
                            (el) => el.url !== args.url
                          ),
                        },
                      }
                    : item
                )
            )
          );
          queryFulfilled.catch(() => {
            updateKnowledgeList.undo();
          });
        },
        invalidatesTags: [AIWorkflowsApiTag.info],
      }),
      recrawlLinkKnowledge: builder.mutation<
        KnowledgeItem<"links">["data"],
        {
          entityId: string;
          agentId: string;
          src_id: string;
          url: string;
        }
      >({
        query: ({ entityId, agentId, src_id, url }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge/recrawl`,
          method: "POST",
          data: {
            src_id,
            url,
          },
          silentError: true,
        }),
        onQueryStarted: patchKnowledgeList<
          "links",
          { entityId: string; agentId: string; src_id: string }
        >({
          knowledgeType: "links",
          patchMode: "move-to-top",
          resolveSourceId: (mutationArgs) => mutationArgs.src_id,
          updateKnowledgeCache,
        }),
        invalidatesTags: [AIWorkflowsApiTag.info],
      }),
      getLinkContent: builder.query<
        {
          content: string;
        },
        {
          entityId: string;
          agentId: string;
          source_id: string;
          url: string;
        }
      >({
        query: ({ entityId, agentId, url, source_id }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge/link/content`,
          params: { url, source_id },
        }),
      }),
      getSiteInfo: builder.query<
        {
          info: {
            logo_base64: string;
            logo_url: string;
            primary_color: string;
            title: string;
          };
        },
        {
          entityId: string;
          agentId: string;
          url: string;
        }
      >({
        query: ({ entityId, agentId, url }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge/site_info`,
          params: { url },
        }),
      }),
      sendAgentChatMessage: builder.mutation<
        {
          agent_response: string;
          cta_tool_result?: ChatBotMessageCTAToolResult;
          quick_replies?: string[];
        },
        {
          entityId: string;
          input: string;
          medium: "whatsapp" | "instagram" | "twilio" | "chatbot";
          business_name: string;
          knowledge_id: string;
          agent_id: string;
          instructions: string;
        }
      >({
        query: ({
          entityId,
          business_name,
          medium,
          input,
          instructions,
          agent_id,
          knowledge_id,
        }) => ({
          url: `/api/${entityId}/ai_agent`,
          method: "POST",
          data: {
            input,
            target_agent: "cx",
            agent_params: {
              medium,
              agent_id,
              business_name,
              knowledge_id,
              instructions,
            },
          },
        }),
      }),
      trainAgent: builder.mutation<
        void,
        {
          entityId: string;
          agentId: string;
          src_id: string[];
        }
      >({
        query: ({ entityId, agentId, src_id }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge/train`,
          method: "POST",
          data: { src_id },
        }),
        onQueryStarted: (
          { entityId, agentId },
          { dispatch, queryFulfilled }
        ) => {
          const patchResult = dispatch(
            aiWorkflowsApi.util.updateQueryData(
              "getTrainingStatus",
              { entityId, agentId },
              (draft) => {
                draft.status = "in_process";
              }
            )
          );

          queryFulfilled.catch(() => {
            patchResult.undo();
          });
        },
      }),
      getTrainingStatus: builder.query<
        { status: "in_process" | "completed" },
        {
          entityId: string;
          agentId: string;
        }
      >({
        query: ({ entityId, agentId }) => ({
          url: `/api/auth/${entityId}/agent/${agentId}/knowledge/train/status`,
        }),
      }),
      createAnonymousSession: builder.mutation<
        {
          data: AnonymousSession;
          token: string;
        },
        {
          unique_id: string;
          grc_token: string;
          agent_info: {
            browser: string;
            os: string;
            device: string;
          };
        }
      >({
        query: ({ agent_info, grc_token, unique_id }) => ({
          url: "/api/anonymous_session",
          method: "POST",
          data: {
            unique_id,
            grc_token,
            agent: "web",
            agent_info,
            type: "cx",
          },
          rawResponse: true,
        }),
        transformResponse: (response: AxiosResponse<AnonymousSession>) => ({
          token: response.headers["x-temp-auth-joiint"],
          data: response.data,
        }),
      }),
    }),
  });

  return aiWorkflowsApi;
};

export type AIWorkflowsApi = ReturnType<typeof addAIWorkflowsApi>;
